import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { RoutineForm } from "./RoutineForm";
import styles from "./Routines.module.css";
import { useSession } from "../../auth/session";
import { useDocumentTitle } from "../../chrome/useDocumentTitle";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Pill } from "../../components/Pill";
import { useCurrentWorkspace } from "../../data/api/currentWorkspace";
import { repoLabel, useRepository } from "../../data/api/repositories";
import {
  type ApiRoutine,
  type ApiRoutineRun,
  deleteRoutine,
  OVERLAP_LABEL,
  pauseLabel,
  runReasonLabel,
  runRoutine,
  updateRoutine,
  useRepoRoutines,
  useRoutineRuns,
} from "../../data/api/routines";
import { toTaskStatus } from "../../data/api/tasks";
import { Icon } from "../../icons";
import { cx } from "../../lib/cx";
import { statusLabel, zonedTime } from "../../lib/time";
import { paths } from "../../routing/paths";
import { ErrorRow, useAction } from "../settings/parts";
import settings from "../settings/Settings.module.css";

// The routines page (routines.md §The routines page).

const TaskLink = ({
  repoId,
  task,
}: {
  readonly repoId: string;
  readonly task: NonNullable<ApiRoutineRun["task"]>;
}) => (
  <Link
    className={cx(settings.link, styles.taskLink, "mono")}
    title={task.title}
    to={paths.task(repoId, task.display_id)}
  >
    {task.display_id}
  </Link>
);

type RunOutcomeProps = {
  readonly repoId: string;
  readonly run: ApiRoutineRun;
};

const RunOutcome = ({ repoId, run }: RunOutcomeProps) => {
  if (run.outcome === "skipped") {
    return <span className={settings.help}>{runReasonLabel(run)}</span>;
  }
  if (!run.task) {
    // The task went, and `on delete set null` took the link with it.
    return <span className={settings.help}>Started</span>;
  }
  const status = toTaskStatus(run.task.status);
  return (
    <span className={styles.outcome}>
      <TaskLink repoId={repoId} task={run.task} />
      <Pill status={status}>{statusLabel(status)}</Pill>
    </span>
  );
};

const RecentRuns = ({ routine }: { readonly routine: ApiRoutine }) => {
  const { loading, runs } = useRoutineRuns(
    routine.repository_id,
    routine.public_id,
  );
  if (loading && runs.length === 0) {
    return <div className={settings.help}>Loading runs…</div>;
  }
  if (runs.length === 0) {
    return <div className={settings.help}>No runs yet.</div>;
  }
  return (
    <ul aria-label="Recent runs" className={styles.runs}>
      {runs.map((run) => (
        <li className={styles.run} key={run.public_id}>
          <span className={styles.runTime}>
            {zonedTime(run.scheduled_for, routine.timezone)}
          </span>
          <span className={styles.runTrigger}>
            {run.trigger === "manual" ? "Run now" : "Scheduled"}
          </span>
          <RunOutcome repoId={routine.repository_id} run={run} />
        </li>
      ))}
    </ul>
  );
};

type RoutineDetailProps = {
  readonly canManage: boolean;
  readonly onChanged: () => void;
  readonly onEdit: () => void;
  readonly routine: ApiRoutine;
};

const RoutineDetail = ({
  canManage,
  onChanged,
  onEdit,
  routine,
}: RoutineDetailProps) => {
  const { busy, error, run } = useAction();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [started, setStarted] = useState<ApiRoutineRun | null>(null);

  const act = (action: () => Promise<unknown>, fallback: string): void =>
    run(() => action().then(onChanged), fallback);

  const settingsLine = [
    OVERLAP_LABEL[routine.overlap],
    routine.model ? `model ${routine.model}` : null,
    routine.effort ? `${routine.effort} effort` : null,
    routine.base_branch ? `from ${routine.base_branch}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className={styles.detail}>
      <pre className={styles.promptText}>{routine.prompt}</pre>
      <div className={settings.help}>{settingsLine}</div>

      {canManage ? (
        <div className={styles.actions}>
          <Button disabled={busy} onClick={onEdit} size="sm">
            Edit
          </Button>
          {/* Neither has anything to do while the owner is gone: a resume
              only pauses it again at the next due time, and Run now is
              refused (routines.md §Ownership, §Run now). */}
          {routine.pause_reason === "owner_left" ? null : (
            <>
              <Button
                disabled={busy}
                onClick={() =>
                  act(
                    () =>
                      updateRoutine(routine.public_id, {
                        paused: !routine.paused,
                      }),
                    "Couldn't change the routine.",
                  )
                }
                size="sm"
              >
                {routine.paused ? "Resume" : "Pause"}
              </Button>
              <Button
                disabled={busy}
                onClick={() => {
                  setStarted(null);
                  act(
                    () => runRoutine(routine.public_id).then(setStarted),
                    "Couldn't start a run.",
                  );
                }}
                size="sm"
              >
                Run now
              </Button>
            </>
          )}
          <span className={settings.grow} />
          {confirmDelete ? (
            <>
              <Button
                onClick={() => setConfirmDelete(false)}
                size="sm"
                variant="ghost"
              >
                Cancel
              </Button>
              <Button
                disabled={busy}
                onClick={() =>
                  act(
                    () => deleteRoutine(routine.public_id),
                    "Couldn't delete the routine.",
                  )
                }
                size="sm"
                variant="danger"
              >
                Delete routine
              </Button>
            </>
          ) : (
            <Button
              onClick={() => setConfirmDelete(true)}
              size="sm"
              variant="ghost"
            >
              Delete
            </Button>
          )}
        </div>
      ) : (
        <div className={settings.help}>
          Only {routine.owner.display_name} or a workspace admin can change this
          routine.
        </div>
      )}
      {routine.pause_reason === "owner_left" ? (
        <div className={settings.help}>
          It can&apos;t be resumed. To keep the work going, create a new
          routine.
        </div>
      ) : null}
      {/* The link only: its status would be the response's, frozen at
          pending, while the runs below follow it live. */}
      {started?.task ? (
        <div className={styles.started} role="status">
          Started{" "}
          <TaskLink repoId={routine.repository_id} task={started.task} />
        </div>
      ) : null}
      {error ? (
        <div className={styles.failure} role="alert">
          {error}
        </div>
      ) : null}

      <div className={styles.runsTitle}>Recent runs</div>
      <RecentRuns routine={routine} />
    </div>
  );
};

type RoutineRowProps = {
  readonly canManage: boolean;
  readonly editing: boolean;
  readonly onChanged: () => void;
  readonly onEdit: (editing: boolean) => void;
  readonly onToggle: () => void;
  readonly open: boolean;
  readonly routine: ApiRoutine;
};

const RoutineRow = ({
  canManage,
  editing,
  onChanged,
  onEdit,
  onToggle,
  open,
  routine,
}: RoutineRowProps) => (
  <div className={styles.routine}>
    <div className={styles.summary}>
      <button
        aria-expanded={open}
        className={styles.toggle}
        onClick={onToggle}
        type="button"
      >
        <Icon.Chevron
          className={cx(styles.chevron, open && styles.chevronOpen)}
          size={12}
        />
        <span className={styles.nameBlock}>
          <span className={settings.label}>{routine.name}</span>
          <span className={settings.help}>
            {routine.schedule_description} ·{" "}
            <code className={styles.cronText}>{routine.schedule}</code> ·{" "}
            {routine.timezone}
          </span>
        </span>
      </button>
      <div className={styles.cell}>
        <span className={styles.caption}>Next run</span>
        {routine.paused || !routine.next_run_at ? (
          <span className={styles.paused}>
            {pauseLabel(routine.pause_reason)}
          </span>
        ) : (
          <span>{zonedTime(routine.next_run_at, routine.timezone)}</span>
        )}
      </div>
      <div className={styles.cell}>
        <span className={styles.caption}>Last run</span>
        {routine.last_run ? (
          <RunOutcome repoId={routine.repository_id} run={routine.last_run} />
        ) : (
          <span className={settings.help}>None yet</span>
        )}
      </div>
      <div className={styles.cell}>
        <span className={styles.caption}>Owner</span>
        <span>{routine.owner.display_name}</span>
      </div>
    </div>
    {editing ? (
      <RoutineForm
        initial={routine}
        onCancel={() => onEdit(false)}
        onSaved={() => {
          onEdit(false);
          onChanged();
        }}
        repoId={routine.repository_id}
      />
    ) : open ? (
      <RoutineDetail
        canManage={canManage}
        onChanged={onChanged}
        onEdit={() => onEdit(true)}
        routine={routine}
      />
    ) : null}
  </div>
);

export const RoutinesView = () => {
  const { repoId = "" } = useParams();
  const {
    error: repoError,
    loading: repoLoading,
    repository,
  } = useRepository(repoId || null);
  const { error, loading, refetch, routines } = useRepoRoutines(repoId || null);
  const { isAdmin } = useCurrentWorkspace();
  const { user } = useSession();
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  // Opening or closing a row ends any edit, as Cancel does: a form left under
  // a collapsed row is a draft lost the next time Edit is clicked.
  const openRow = (id: string | null): void => {
    setEditing(null);
    setOpen(id);
  };
  useDocumentTitle("Routines", repository ? repoLabel(repository) : repoId);

  if (repoLoading && !repository) {
    return <div className={settings.repoLoading}>Loading routines…</div>;
  }
  if (repoError || !repository) {
    return (
      <EmptyState
        body="This repository isn't connected, or the link is stale."
        title="Repository not found."
      />
    );
  }

  // routines.md §Permissions: any member may create one; only its owner or an
  // admin may change it.
  const canManage = (routine: ApiRoutine): boolean =>
    isAdmin || routine.owner.public_id === user?.public_id;
  const newButton = (
    <Button onClick={() => setCreating(true)} size="sm" variant="accent">
      <Icon.Plus size={13} /> New routine
    </Button>
  );

  return (
    <div className={settings.settings}>
      <div className={settings.header}>
        <div>
          <h1 className={settings.title}>Routines</h1>
          <div className={settings.help}>
            Agents started on a schedule. Each run is an ordinary task, owned by
            whoever created the routine.
          </div>
        </div>
        {creating || routines.length === 0 ? null : newButton}
      </div>

      {creating ? (
        <section className={settings.section}>
          <h3 className={settings.sectionTitle}>New routine</h3>
          <div className={settings.card}>
            <RoutineForm
              onCancel={() => setCreating(false)}
              onSaved={(routine) => {
                setCreating(false);
                openRow(routine.public_id);
                refetch();
              }}
              repoId={repository.id}
            />
          </div>
        </section>
      ) : null}

      {error && routines.length === 0 ? (
        <div className={settings.card}>
          <ErrorRow message={error.message} />
        </div>
      ) : loading && routines.length === 0 ? (
        <div className={settings.repoLoading}>Loading routines…</div>
      ) : routines.length === 0 ? (
        creating ? null : (
          <EmptyState
            action={newButton}
            body="A routine starts an agent on this repository on a schedule, with the same prompt each time."
            title="No routines yet."
          />
        )
      ) : (
        <div className={settings.card}>
          {routines.map((routine) => (
            <RoutineRow
              canManage={canManage(routine)}
              editing={editing === routine.public_id}
              key={routine.public_id}
              onChanged={refetch}
              onEdit={(on) => setEditing(on ? routine.public_id : null)}
              onToggle={() =>
                openRow(open === routine.public_id ? null : routine.public_id)
              }
              open={open === routine.public_id}
              routine={routine}
            />
          ))}
        </div>
      )}
    </div>
  );
};
